import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import RoleService from '../services/RoleService';

// Permission checking interface
interface PermissionsContextType {
  permissions: string[];
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  canAccess: (requiredPermissions: string[], requireAll?: boolean) => boolean;
  refreshPermissions: () => Promise<void>;
}

// Create permissions context
const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// Permissions provider props
interface PermissionsProviderProps {
  children: ReactNode;
}

// Permissions provider component
export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  // Load user permissions when auth state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserPermissions();
    } else {
      setPermissions([]);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const loadUserPermissions = async () => {
    try {
      setLoading(true);
      
      if (!user?.role) {
        setPermissions([]);
        return;
      }

      // For admin users, get all available permissions
      if (user.role === 'admin') {
        const permissionsData = await RoleService.getPermissions();
        setPermissions(permissionsData.permissions);
      } else {
        // For non-admin users, use role-based permissions
        setPermissions(getDefaultPermissionsByRole(user.role));
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
      // Fallback to role-based permissions if API fails
      setPermissions(getDefaultPermissionsByRole(user?.role || ''));
    } finally {
      setLoading(false);
    }
  };

  // Fallback function for default permissions when API is unavailable
  const getDefaultPermissionsByRole = (role: string): string[] => {
    switch (role) {
      case 'admin':
        return [
          'user.view', 'user.create', 'user.edit', 'user.delete',
          'role.view', 'role.assign', 'role.manage',
          'project.view', 'project.create', 'project.edit', 'project.delete', 'project.assign', 'project.status.update',
          'task.view', 'task.create', 'task.edit', 'task.delete', 'task.comment', 'task.attach', 'task.time.log',
          'team.view', 'team.manage',
          'chat.view', 'chat.post', 'document.share', 'document.manage',
          'client.view', 'client.edit',
          'invoice.view', 'invoice.create', 'invoice.manage',
          'audit.view', 'settings.view', 'settings.manage', 'api.manage', 'backup.run'
        ];
      case 'project_manager_lead':
        return [
          'user.view',
          'project.view', 'project.create', 'project.edit', 'project.assign', 'project.status.update',
          'task.view', 'task.create', 'task.edit', 'task.delete', 'task.comment', 'task.attach', 'task.time.log',
          'team.view', 'team.manage',
          'chat.view', 'chat.post', 'document.share', 'document.manage',
          'client.view',
          'invoice.view'
        ];
      case 'project_manager':
        return [
          'user.view',
          'project.view', 'project.create', 'project.edit', 'project.assign', 'project.status.update',
          'task.view', 'task.create', 'task.edit', 'task.delete', 'task.comment', 'task.attach', 'task.time.log',
          'team.view', 'team.manage',
          'chat.view', 'chat.post', 'document.share', 'document.manage',
          'client.view',
          'invoice.view'
        ];
      case 'developer_lead':
        return [
          'project.view',
          'task.view', 'task.edit', 'task.comment', 'task.attach', 'task.time.log',
          'team.view',
          'chat.view', 'chat.post', 'document.share'
        ];
      case 'developer':
        return [
          'project.view',
          'task.view', 'task.edit', 'task.comment', 'task.attach', 'task.time.log',
          'team.view',
          'chat.view', 'chat.post', 'document.share'
        ];
      case 'client':
        return [
          'project.view', 'project.create', 'project.edit',
          'task.view', 'task.comment',
          'chat.view', 'chat.post',
          'communication.view',
          'invoice.view'
        ];
      default:
        return [];
    }
  };

  const refreshPermissions = async () => {
    await loadUserPermissions();
  };

  // Permission checking methods
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList: string[]): boolean => {
    return permissionList.every(permission => permissions.includes(permission));
  };

  const canAccess = (requiredPermissions: string[], requireAll: boolean = false): boolean => {
    if (requiredPermissions.length === 0) return true;
    
    return requireAll 
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);
  };

  const contextValue: PermissionsContextType = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    refreshPermissions,
  };

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Hook to use permissions context
export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// HOC for permission-based route protection
interface PermissionGuardProps {
  children: ReactNode;
  requiredPermissions: string[];
  requireAll?: boolean;
  fallback?: ReactNode;
  showLoading?: boolean;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  requiredPermissions,
  requireAll = false,
  fallback = <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
    You don't have permission to access this content.
  </div>,
  showLoading = true,
}) => {
  const { canAccess, loading } = usePermissions();

  if (loading && showLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading permissions...</div>;
  }

  if (!canAccess(requiredPermissions, requireAll)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Utility hook for conditional rendering based on permissions
export const useConditionalRender = () => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, canAccess } = usePermissions();

  const renderIfPermission = (permission: string, component: ReactNode) => {
    return hasPermission(permission) ? component : null;
  };

  const renderIfAnyPermission = (permissions: string[], component: ReactNode) => {
    return hasAnyPermission(permissions) ? component : null;
  };

  const renderIfAllPermissions = (permissions: string[], component: ReactNode) => {
    return hasAllPermissions(permissions) ? component : null;
  };

  const renderIfCanAccess = (requiredPermissions: string[], component: ReactNode, requireAll: boolean = false) => {
    return canAccess(requiredPermissions, requireAll) ? component : null;
  };

  return {
    renderIfPermission,
    renderIfAnyPermission,
    renderIfAllPermissions,
    renderIfCanAccess,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
  };
};

export default PermissionsContext;